import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Fetch_product_products } from "../../../hooks/APIHooks";
import { BsBag } from "react-icons/bs";
import { FaStar } from "react-icons/fa";

import lazy_loaderwebp from "../../../assets/lazy_loaderwebp.webp";

const ProductList = () => {
  const { subCatalogueId } = useParams();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imageLoaded, setImageLoaded] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${Fetch_product_products}?sub_catalogue_id=${subCatalogueId}`
        );
        const data = await response.json();
        if (data.success) {
          setProducts(data.data);
        } else {
          throw new Error(data.error || "Failed to fetch products");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [subCatalogueId]);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const handleProductClick = (productId) => {
    navigate(`/Store/sub-catalogue/products/product-details/${productId}`);
  };

  const handleImageLoad = (id) => {
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
    setImageErrors((prev) => ({ ...prev, [id]: false }));
  };

  const handleImageError = (id) => {
    setImageErrors((prev) => ({ ...prev, [id]: true }));
    setImageLoaded((prev) => ({ ...prev, [id]: true }));
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} className="text-orange-500" />);
    }
    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="text-orange-500 opacity-50" />);
    }
    for (let i = fullStars + (hasHalfStar ? 1 : 0); i < 5; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="text-gray-300" />);
    }

    return stars;
  };

  const totalItemsInCart = cart.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const getFallbackContent = (product) => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-600 mb-2">
            {product.product_name ? product.product_name.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="text-xs text-gray-500 px-2">
            {product.product_name || 'No Name'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto">
      {loading ? (
        <div className="text-center text-lg py-8">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          No approved products found.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 p-4">
          {products.map((product) => (
            <div
              key={product.product_id}
              className="relative flex flex-col items-center text-center cursor-pointer p-2 border rounded-lg hover:shadow-lg transition-shadow duration-300 bg-white"
              onClick={() => handleProductClick(product.product_id)}
            >
              <div className="relative w-full h-42 sm:h-58 overflow-hidden rounded-lg">
                {/* Lazy Loader */}
                <img
                  src={lazy_loaderwebp}
                  alt="Loading..."
                  className="object-cover object-top w-full h-full absolute top-0 left-0 transition-opacity duration-500"
                  style={{ opacity: imageLoaded[product.product_id] ? 0 : 1 }}
                />
                
                {/* Main Image or Fallback */}
                {product.product_image && !imageErrors[product.product_id] ? (
                  <img
                    src={getImageUrl(product.product_image)}
                    alt={product.product_name}
                    loading="lazy"
                    onLoad={() => handleImageLoad(product.product_id)}
                    onError={() => handleImageError(product.product_id)}
                    className="object-cover object-top w-full h-full transition-opacity duration-500 hover:scale-105 transition-transform duration-300"
                    style={{ opacity: imageLoaded[product.product_id] ? 1 : 0 }}
                  />
                ) : (
                  getFallbackContent(product)
                )}
              </div>
              
              <div className="p-2 w-full">
                <div className="flex items-center">
                  <p
                    className="font-medium mx-auto text-sm sm:text-base line-clamp-2 overflow-hidden text-ellipsis"
                    title={product.product_name || "Unknown Product"}
                  >
                    {product.product_name || "Unknown Product"}
                  </p>
                </div>
                
                <div className="flex items-center justify-between p-2 border-b border-gray-200">
                  <span
                    className="text-gray-600 text-xs sm:text-sm line-clamp-1 overflow-hidden text-ellipsis"
                    title={product.product_description || "No description available"}
                  >
                    {product.product_description || "No description available"}
                  </span>
                </div>
                
                {/* Render price and variants */}
                {product.product_type === "single" && (
                  <div className="flex flex-col mt-2">
                    {product.sale_price && product.sale_price < product.price && (
                      <div>
                        <p className="text-[12px] font-semibold text-gray-500 leading-tight">
                          M.R.P
                        </p>
                        <p className="text-[13px] font-semibold text-black line-through">
                          ₹ {product.price}
                        </p>
                      </div>
                    )}
                    {product.sale_price > 0 && (
                      <div>
                        <p className="text-[12px] mx-auto bg-green-600 px-3 py-1 max-w-fit rounded-md font-semibold text-white leading-tight border-dashed border-2 border-white relative">
                          Sale Price
                        </p>
                        <p className="text-[18px] font-semibold text-black">
                          ₹ {product.sale_price}
                        </p>
                      </div>
                    )}
                    {product.sale_price <= 0 && (
                      <p className="text-[18px] font-semibold text-black">
                        ₹ {product.price}
                      </p>
                    )}
                  </div>
                )}

                {/* Render variants for multivariate products */}
                {product.product_type === "multivariate" &&
                  product.variants &&
                  product.variants.length > 0 && (
                    <div className="my-2 px-2 text-[14px]">
                      <h3 className="font-bold text-sm">Variant:</h3>
                      <div className="flex flex-col">
                        {product.variants[0].variant_sale_prices > 0 && (
                          <div>
                            <p className="text-[12px] font-semibold text-gray-500 leading-tight">
                              M.R.P
                            </p>
                            <p className="text-[13px] font-semibold text-black line-through">
                              ₹{product.variants[0].variant_prices}
                            </p>
                            <p className="text-[12px] mx-auto bg-green-600 px-3 py-1 max-w-fit rounded-md font-semibold text-white leading-tight border-dashed border-2 border-white relative">
                              Sale Price
                            </p>
                            <p className="text-[18px] font-semibold text-black">
                              ₹{product.variants[0].variant_sale_prices}
                            </p>
                          </div>
                        )}
                        {product.variants[0].variant_sale_prices <= 0 && (
                          <p className="text-[18px] font-semibold text-black">
                            ₹{product.variants[0].variant_prices}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                <p className="text-sm flex mx-auto items-center justify-center rounded-full w-fit text-white py-1 space-x-1 mt-2">
                  {renderStars(product.product_rating)}{" "}
                  <span className="text-black">({product.product_rating})</span>
                </p>
                
                <button
                  className="bg-primary mx-auto flex items-center gap-2 rounded-full shadow-md text-black text-sm font-medium px-4 py-2 transition-all hover:scale-105 hover:shadow-lg mt-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProductClick(product.product_id);
                  }}
                >
                  <BsBag size={20} />
                  Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductList;